import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Box, Button, Group, Loader, Stack, Switch, Text } from '@mantine/core';
import { publicMessageOf, useTRPC } from '../lib/trpc';
import { useCan } from '../lib/session';
import classes from '../pages/EntityPage.module.css';

/**
 * Виливка сайту — термінальна дія всього продукту, і до 15.08 її не було в інтерфейсі.
 *
 * `deploy.preview` і `deploy.run` існували, працювали й **не мали жодного викликача**: CRM
 * уміла завести домен, зібрати сторінки й показати чекліст готовності — а виливати доводилось
 * повз неї. Мокап при цьому закінчує візард саме кнопкою «Задеплоїти сайт».
 *
 * Один компонент на два місця (візард і картка сайту), а не дві копії: виливка — операція, у
 * якої мусить бути один набір запобіжників, і друга копія розійшлася б із першою саме в них.
 *
 * Прев'ю показується **до** кнопки, а не після натискання: розмір сторінок і перевищення
 * бюджету — це те, що вирішує, тиснути чи ні. Перевищення не блокує, але вимагає явного
 * «приймаю»: рендер, більший за бюджет, іноді законний (довгий огляд), а іноді означає, що
 * блок зациклився — і другого випадку не видно, якщо кнопка просто спрацювала.
 */

export interface DeployPanelProps {
  siteId: string;
  /** `false` — чекліст має відмови; кнопка лишається видимою й неактивною, з причиною. */
  deployable: boolean;
  testId?: string;
}

export function DeployPanel({
  siteId,
  deployable,
  testId = 'deploy-panel',
}: DeployPanelProps): React.JSX.Element {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const canDeploy = useCan('hosting', 'execute');
  const [acceptOverBudget, setAcceptOverBudget] = useState(false);

  const preview = useQuery(trpc.deploy.preview.queryOptions({ siteId }));
  const run = useMutation(
    trpc.deploy.run.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: trpc.sites.pathKey() });
        await queryClient.invalidateQueries({ queryKey: trpc.deploy.pathKey() });
      },
    }),
  );

  const overBudget = preview.data?.overBudget ?? false;
  const blocked = !deployable || (overBudget && !acceptOverBudget);

  return (
    <Box className={classes.panel} data-testid={testId}>
      <Text className={classes.panelTitle} mb={8}>
        Виливка
      </Text>

      {preview.error ? (
        <Text className={classes.danger} data-testid={`${testId}-preview-error`} role="alert">
          {publicMessageOf(preview.error)}
        </Text>
      ) : preview.data === undefined ? (
        <Loader size="sm" />
      ) : (
        <Stack gap={6}>
          <Group gap="lg">
            <Text className={classes.dim} data-testid={`${testId}-pages`}>
              сторінок: {preview.data.pages.length}
            </Text>
            <Text className={classes.dim} data-testid={`${testId}-bytes`}>
              обсяг: {(preview.data.totalBytes / 1024).toFixed(1)} КБ
            </Text>
            {overBudget ? (
              <Badge size="xs" color="amber" data-testid={`${testId}-over-budget`}>
                перевищено бюджет
              </Badge>
            ) : null}
          </Group>

          {/*
            * Перелік лише тих сторінок, що вилізли за бюджет: показувати всі сорок означало б
            * сховати ті дві, заради яких панель і дивляться.
            */}
          {preview.data.pages
            .filter((page) => page.overBudget)
            .map((page) => (
              <Text
                key={page.url}
                className={classes.warn}
                data-testid={`${testId}-heavy-${page.url}`}
              >
                {page.url} — {(page.bytes / 1024).toFixed(1)} КБ
              </Text>
            ))}

          {overBudget ? (
            <Switch
              size="xs"
              label="Приймаю перевищення бюджету"
              checked={acceptOverBudget}
              data-testid={`${testId}-accept-over-budget`}
              onChange={(event) => {
                setAcceptOverBudget(event.currentTarget.checked);
              }}
            />
          ) : null}
        </Stack>
      )}

      {canDeploy ? (
        <Group gap="sm" mt="sm">
          <Button
            size="xs"
            loading={run.isPending}
            disabled={blocked}
            data-testid={`${testId}-run`}
            onClick={() => {
              run.mutate({
                siteId,
                ...(acceptOverBudget ? { allowOverBudget: true } : {}),
              });
            }}
          >
            Задеплоїти сайт
          </Button>
          {/*
            * Причина відмови стоїть поруч із неактивною кнопкою, а не ховається в тултип:
            * оператор, який бачить сіру кнопку без пояснення, тисне її ще п'ять разів.
            */}
          {blocked ? (
            <Text className={classes.dim} data-testid={`${testId}-blocked`}>
              {deployable
                ? 'Рендер більший за бюджет — підтвердьте перевищення'
                : 'Чекліст має відмови — спершу закрийте їх'}
            </Text>
          ) : null}
        </Group>
      ) : (
        <Text className={classes.dim} mt="sm" data-testid={`${testId}-no-permission`}>
          Виливка потребує права «hosting: execute».
        </Text>
      )}

      {run.error === null ? null : (
        <Text className={classes.danger} mt={6} data-testid={`${testId}-error`} role="alert">
          {publicMessageOf(run.error)}
        </Text>
      )}
      {run.data === undefined ? null : (
        <Text className={classes.mono} mt={6} data-testid={`${testId}-result`}>
          вилито {run.data.filesUploaded} файлів на {run.data.domain}
          {run.data.pairedDomain === null ? '' : ` і ${run.data.pairedDomain}`}
        </Text>
      )}
    </Box>
  );
}
